import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Modal, message, Tag, Form, Input, DatePicker, Select, Upload, Card, Row, Col, InputNumber, Switch, Checkbox, Dropdown } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, UploadOutlined, DownloadOutlined, QrcodeOutlined, SearchOutlined, NumberOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { UploadProps } from 'antd';
import dayjs from 'dayjs';
import { Project, Survey, User, getProjects, createProject, updateProject, deleteProject, exportTemplate, createSurveyExportTask, getSurveyExportTask, downloadSurveyExportTask, importDoctors, getSurveyList, getUsers, uploadAgreementFile, uploadImage } from '../services/project';
import { authService } from '../services/authService';
import ProjectQRCode from '../components/ProjectQRCode';
import ProjectMembers from '../components/ProjectMembers';
import DoctorQuotaDrawer from '../components/DoctorQuotaDrawer';

// 添加导入失败数据的类型定义
interface ImportErrorField {
  value: string;
  error: string;
}

interface ImportErrorDetails {
  type: 'duplicate_id_card' | 'existing_id_card' | 'validation_error';
  fields?: Record<string, ImportErrorField>;
  duplicateRow?: number;
}

interface ImportFailedData {
  rowNumber: number;
  data: {
    '医生姓名'?: string;
    '医生联系电话'?: string;
    '医生身份证号'?: string;
    '医院名称'?: string;
    '科室名称'?: string;
    [key: string]: string | undefined;
  };
  reason: string;
  details: ImportErrorDetails;
}

const { RangePicker } = DatePicker;
const { TextArea } = Input;
const { Option } = Select;

const ProjectManagement: React.FC = () => {
  const [memberProject, setMemberProject] = useState<Project | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [surveys, setSurveys] = useState<Survey[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [currentProject, setCurrentProject] = useState<Project | null>(null);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [exportingProjectId, setExportingProjectId] = useState<number | null>(null);
  
  // 获取当前用户信息
  const currentUser = authService.getCurrentUser();
  const isProjectAdmin = currentUser?.role === 'project_admin';
  const isSuperAdmin = currentUser?.role === 'super_admin';
  // QR码相关状态
  const [qrCodeModalVisible, setQrCodeModalVisible] = useState(false);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  // 医生配额抽屉状态
  const [quotaDrawerOpen, setQuotaDrawerOpen] = useState(false);
  const [quotaProject, setQuotaProject] = useState<Project | null>(null);
  // 问卷搜索状态
  const [surveySearchText, setSurveySearchText] = useState<string>('');
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);
  const [surveyLoading, setSurveyLoading] = useState<boolean>(false);
  // 批量选择问卷相关状态
  const [surveyBatchModalVisible, setSurveyBatchModalVisible] = useState(false);
  const [batchSurveys, setBatchSurveys] = useState<Survey[]>([]);
  const [selectedSurveyKeys, setSelectedSurveyKeys] = useState<React.Key[]>([]);
  const [surveyFilterText, setSurveyFilterText] = useState<string>('');
  const [surveyTypeFilter, setSurveyTypeFilter] = useState<string>('');
  const [batchLoading, setBatchLoading] = useState(false);

  // 获取项目列表
  const fetchProjects = async () => {
    try {
      setLoading(true);
      const response = await getProjects();
      if (response.success) {
        setProjects(response.data);
      }
    } catch (error) {
      message.error('获取项目列表失败');
    } finally {
      setLoading(false);
    }
  };

  // 获取问卷列表
  const fetchSurveys = async (searchText?: string) => {
    try {
      setSurveyLoading(true);
      const searchQuery = searchText !== undefined ? searchText : surveySearchText;
      const response = await getSurveyList(searchQuery);
      if (response.success && response.data) {
        setSurveys(response.data);
      } else {
        setSurveys([]);
      }
    } catch (error) {
      console.error('获取问卷列表失败:', error);
      message.error('获取问卷列表失败');
      setSurveys([]);
    } finally {
      setSurveyLoading(false);
    }
  };

  // 批量获取问卷列表（用于批量选择）
  const fetchBatchSurveys = async () => {
    try {
      setBatchLoading(true);
      const response = await getSurveyList(''); // 获取所有问卷
      if (response.success && response.data) {
        setBatchSurveys(response.data);
      } else {
        setBatchSurveys([]);
      }
    } catch (error) {
      console.error('获取问卷列表失败:', error);
      message.error('获取问卷列表失败');
      setBatchSurveys([]);
    } finally {
      setBatchLoading(false);
    }
  };

  // 获取用户列表
  const fetchUsers = async () => {
    try {
      console.log('开始获取用户列表...');
      const response = await getUsers();
      console.log('用户列表API响应:', response);
      if (response.success && response.data) {
        console.log('设置用户数据:', response.data);
        setUsers(response.data);
      } else {
        console.log('用户列表为空或API失败');
        setUsers([]);
      }
    } catch (error) {
      console.error('获取用户列表失败:', error);
      message.error('获取用户列表失败');
      setUsers([]);
    }
  };


  useEffect(() => {
    console.log('ProjectManagement组件加载，开始获取数据...');
    fetchProjects();
    fetchSurveys();
    fetchUsers();
    
    // 清理函数，组件卸载时清除定时器
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, []);

  const columns: ColumnsType<Project> = [
    { title: '审核方式', dataIndex: 'review_mode', width: 110, render: mode => <Tag color={mode === 'ai' ? 'blue' : 'orange'}>{mode === 'ai' ? 'AI审核' : '人工审核'}</Tag> },
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
    },
    {
      title: '项目名称',
      dataIndex: 'name',
      key: 'name',
      width: 150,
      ellipsis: true,
    },
    {
      title: '公司名称',
      dataIndex: 'company_name',
      key: 'company_name',
      width: 120,
      ellipsis: true,
    },
    {
      title: '项目周期',
      key: 'period',
      width: 180,
      render: (_, record) => (
        <span>
          {dayjs(record.startDate).format('YYYY-MM-DD')} 至 {dayjs(record.endDate).format('YYYY-MM-DD') }
        </span>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: (status: string) => {
        const statusMap = {
          planning: { color: 'blue', text: '计划中' },
          ongoing: { color: 'green', text: '进行中' },
          completed: { color: 'gray', text: '已完成' },
          cancelled: { color: 'red', text: '已取消' },
        };
        return (
          <Tag color={statusMap[status as keyof typeof statusMap].color}>
            {statusMap[status as keyof typeof statusMap].text}
          </Tag>
        );
      },
    },
    {
      title: '项目类型',
      dataIndex: 'project_type',
      key: 'project_type',
      width: 100,
      render: (projectType: string) => {
        const typeMap = {
          survey: { color: 'blue', text: '问卷项目' },
          ambassador: { color: 'orange', text: '大使项目' },
        };
        const typeInfo = typeMap[projectType as keyof typeof typeMap] || { color: 'gray', text: '未知' };
        return (
          <Tag color={typeInfo.color}>
            {typeInfo.text}
          </Tag>
        );
      },
    },
    {
      title: '项目经理',
      dataIndex: 'manager',
      key: 'manager',
      width: 80,
    },
    {
      title: '预算',
      dataIndex: 'budget',
      key: 'budget',
      width: 100,
      render: (budget: number) => `¥${budget.toLocaleString()}`,
    },
    {
      title: '操作',
      key: 'action',
      fixed: 'right',
      width: 500,
      render: (_, record) => {
        // project_admin 只能操作自己负责的项目
        const canOperate = isSuperAdmin || (isProjectAdmin && record.manager === currentUser?.name);
        
        return (
          <Space size="small">
            <Button
              type="primary"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              disabled={!canOperate}
            >
              编辑
            </Button>
            <Button
              type="primary"
              danger
              size="small"
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record.id)}
              disabled={!canOperate}
            >
              删除
            </Button>
            <Button
              size="small"
              icon={<QrcodeOutlined />}
              onClick={() => handleShowQRCode(record)}
            >
              二维码
            </Button>
            <Button
              size="small"
              icon={<DownloadOutlined />}
              onClick={() => handleExportTemplate(record.id)}
              disabled={!canOperate}
            >
              导出白名单
            </Button>
            {record.project_type === 'survey' && (
              <Dropdown
                disabled={!canOperate}
                menu={{
                  items: [
                    {
                      key: 'desensitized',
                      label: '脱敏导出（不含姓名/电话/医院）',
                      onClick: () => handleExportSurveyAnswers(record, 'desensitized')
                    },
                    {
                      key: 'full',
                      label: '完整导出（含姓名/电话/医院）',
                      danger: true,
                      onClick: () => handleConfirmFullExport(record)
                    }
                  ]
                }}
              >
                <Button
                  size="small"
                  icon={<DownloadOutlined />}
                  disabled={!canOperate}
                  loading={exportingProjectId === record.id}
                >
                  导出问卷
                </Button>
              </Dropdown>
            )}
            <Button
              size="small"
              icon={<NumberOutlined />}
              onClick={() => handleOpenQuotaDrawer(record)}
              disabled={!canOperate}
            >
              医生配额
            </Button>
            <Button onClick={() => setMemberProject(record)}>人员分配</Button>
            <Upload
              accept=".xlsx,.xls"
              showUploadList={false}
              customRequest={handleImportDoctors(record.id)}
              disabled={!canOperate}
            >
              <Button 
                size="small" 
                icon={<UploadOutlined />}
                disabled={!canOperate}
              >
                导入白名单
              </Button>
            </Upload>
          </Space>
        );
      },
    },
  ];

  const handleConfirmFullExport = (project: Project) => {
    Modal.confirm({
      title: '导出完整问卷数据',
      okText: '我已确认，继续导出',
      okButtonProps: { danger: true },
      cancelText: '取消',
      content: (
        <div>
          <p>
            即将导出「{project.name}」的完整问卷数据，文件中会包含医生姓名、手机号和医院名称等个人敏感信息。
          </p>
          <p style={{ marginBottom: 0, color: '#fa8c16' }}>
            本次操作会记录导出日志。请确认你有权获取这些信息，并妥善保管导出的文件，不要转发给无关人员。
          </p>
        </div>
      ),
      onOk: () => handleExportSurveyAnswers(project, 'full')
    });
  };

  const handleOpenQuotaDrawer = (project: Project) => {
    setQuotaProject(project);
    setQuotaDrawerOpen(true);
  };

  const handleCloseQuotaDrawer = () => {
    setQuotaDrawerOpen(false);
    setQuotaProject(null);
  };

  const handleAdd = () => {
    setCurrentProject(null);
    form.resetFields();
    // 设置默认值
    const defaultValues = {
      require_login: true,
      agreement_required: false,
      agreement_input_type: 'text',
      review_mode: 'manual',
      approval_mode: 'any',
      use_role_based_approval: false,
      level_config: ['省份', '城市'],
      status: 'planning',
      project_type: 'survey', // 默认为问卷项目
      participation_limit_type: 'unlimited', // 默认无限制
      participation_max_times: 1, // 默认1次
      manager: '' // 添加manager字段
    };
    
    // 如果是project_admin，自动设置为当前用户
    if (isProjectAdmin) {
      defaultValues.manager = currentUser?.name || '';
    }
    
    form.setFieldsValue(defaultValues);
    setIsModalVisible(true);
  };

  const handleEdit = (project: Project) => {
    setCurrentProject(project);
    
    // 判断协议内容是文件URL还是文本
    let agreementInputType = 'text';
    
    if (project.agreement_content) {
      // 如果是URL（包含http或以.pdf结尾），则认为是文件
      if (project.agreement_content.startsWith('http') || project.agreement_content.endsWith('.pdf')) {
        agreementInputType = 'file';
      }
    }
    
    form.setFieldsValue({
      ...project,
      use_role_based_approval: false,
      review_mode: project.review_mode || 'manual',
      dateRange: [dayjs(project.startDate), dayjs(project.endDate)],
      level_config: project.level_config || [],
      agreement_input_type: agreementInputType,
      // 大使项目相关字段
      project_type: project.project_type || 'survey',
      participation_limit_type: project.participation_limit_type || 'unlimited',
      participation_max_times: project.participation_max_times || 1,
      surveys: project.surveys?.map(survey => ({
        survey_id: survey.survey_id,
        order: survey.order,
        is_required: survey.is_required,
        max_times: survey.max_times,
        is_active: survey.is_active
      })) || [],
      approvers: project.approvers?.filter((approver: any) => approver.approver_source === 'user').map((approver: any) => ({
        user_id: approver.user_id,
        approver_type: approver.approver_type,
        order_index: approver.order_index
      })) || [],
      role_approvers: project.approvers?.filter((approver: any) => approver.approver_source === 'role').map((approver: any) => ({
        role_name: approver.role_name,
        approver_type: approver.approver_type,
        order_index: approver.order_index
      })) || []
    });
    setIsModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    Modal.confirm({
      title: '确认删除',
      content: '确定要删除这个项目吗？',
      onOk: async () => {
        try {
          const response = await deleteProject(id);
          if (response.success) {
            message.success('删除成功');
            fetchProjects();
          }
        } catch (error) {
          message.error('删除失败');
        }
      },
    });
  };

  // 显示QR码
  const handleShowQRCode = (project: Project) => {
    setSelectedProject(project);
    setQrCodeModalVisible(true);
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      const { dateRange, agreement_input_type, approvers, role_approvers, ...rest } = values;
      console.log('handleModalOk', values);
      let agreementContent = '';
      
      // 根据输入类型处理协议内容
      if (agreement_input_type === 'file') {
        // 直接使用第一次上传后保存在表单中的URL
        agreementContent = rest.agreement_content || '';
        if (!agreementContent) {
          message.error('请先上传协议文件');
          return;
        }
      } else {
        // 文本模式，直接使用agreement_content字段的值
        agreementContent = rest.agreement_content || '';
      }
      
      const processedApprovers = (approvers || []).map((a: any, index: number) => ({ approver_source: 'user', user_id: a.user_id, approver_type: 'primary', order_index: index + 1 }));
      const projectData = {
        ...rest,
        approval_mode: 'any' as const,
        use_role_based_approval: false,
        startDate: dateRange[0].format('YYYY-MM-DD'),
        endDate: dateRange[1].format('YYYY-MM-DD'),
        agreement_content: agreementContent,
        approvers: processedApprovers // 统一使用 approvers 字段传递给后端
      };

      if (currentProject) {
        const response = await updateProject(currentProject.id, projectData);
        if (response.success) {
          message.success('更新成功');
          setIsModalVisible(false);
          fetchProjects();
        }
      } else {
        const response = await createProject(projectData);
        if (response.success) {
          message.success('添加成功');
          setIsModalVisible(false);
          fetchProjects();
        }
      }
    } catch (error) {
      console.error('操作失败:', error);
      message.error((error as any).response?.data?.message || '操作失败');
    }
  };

  const handleExportTemplate = async (projectId: number) => {
    try {
      const project = projects.find(p => p.id === projectId);
      const blob = await exportTemplate(projectId);
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${project?.name || '项目'}白名单模板.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      message.error('导出模板失败');
    }
  };

  const handleExportSurveyAnswers = async (
    project: Project,
    mode: 'desensitized' | 'full' = 'desensitized'
  ) => {
    try {
      setExportingProjectId(project.id);
      const createResult = await createSurveyExportTask(project.id, mode);
      const taskId = createResult.data.taskId;
      message.info(createResult.message || '导出任务已创建，正在后台生成');

      let task = (await getSurveyExportTask(taskId)).data;
      while (task.status === 'pending' || task.status === 'processing') {
        await new Promise(resolve => window.setTimeout(resolve, 2000));
        task = (await getSurveyExportTask(taskId)).data;
      }

      if (task.status === 'failed') {
        throw new Error(task.errorMessage || '生成导出文件失败');
      }
      if (task.status === 'expired') {
        throw new Error('导出文件已过期，请重新导出');
      }
      if (task.status !== 'completed') {
        throw new Error('导出任务状态异常');
      }

      const blob = await downloadSurveyExportTask(taskId);
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');

      link.href = url;
      link.setAttribute('download', task.filename || `${project.name || `项目${project.id}`}_问卷回答.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      message.success(`问卷导出完成，共生成 ${task.sheetCount || 0} 个工作表`);
    } catch (error: any) {
      let errorMessage = '导出问卷失败';
      const responseData = error?.response?.data;

      if (responseData instanceof Blob) {
        try {
          const errorBody = JSON.parse(await responseData.text());
          errorMessage = errorBody.message || errorBody.error || errorMessage;
        } catch (parseError) {
          // 非JSON错误响应使用默认提示
        }
      } else {
        errorMessage = responseData?.message || responseData?.error || error?.message || errorMessage;
      }

      message.error(errorMessage);
    } finally {
      setExportingProjectId(null);
    }
  };

  const handleImportDoctors = (projectId: number): UploadProps['customRequest'] => {
    return async ({ file }) => {
      if (!file) return;
      try {
        const response = await importDoctors(projectId, file as File);
        if (response.success) {
          const { successCount, failedCount, failedData } = response.data;
          const columns: ColumnsType<ImportFailedData> = [
            {
              title: '行号',
              dataIndex: 'rowNumber',
              key: 'rowNumber',
              width: 80,
            },
            {
              title: '医生信息',
              key: 'doctor',
              render: (_, record: ImportFailedData) => (
                <div>
                  <div>姓名：{record.data?.['医生姓名'] || '-'}</div>
                  <div>电话：{record.data?.['医生联系电话'] || '-'}</div>
                  <div>身份证：{record.data?.['医生身份证号'] || '-'}</div>
                </div>
              ),
            },
            {
              title: '医院/科室',
              key: 'hospital',
              render: (_, record: ImportFailedData) => (
                <div>
                  <div>{record.data?.['医院名称'] || '-'}</div>
                  <div>{record.data?.['科室名称'] || '-'}</div>
                </div>
              ),
            },
            {
              title: '错误原因',
              key: 'reason',
              render: (_, record: ImportFailedData) => (
                <div>
                  <div style={{ color: 'red' }}>{record.reason || '未知错误'}</div>
                  {record.details?.type === 'validation_error' && record.details?.fields && (
                    <div style={{ marginTop: 8 }}>
                      {Object.entries(record.details.fields).map(([field, error]) => (
                        <div key={field} style={{ color: '#ff4d4f' }}>
                          {field}: {error.error}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ),
            },
          ];

          Modal.success({
            title: '导入结果',
            content: (
              <div>
                <p>成功导入: {successCount} 条记录</p>
                <p>失败: {failedCount} 条记录</p>
                {failedCount > 0 && (
                  <div style={{ marginTop: 16 }}>
                    <p>失败详情：</p>
                    <Table<ImportFailedData>
                      dataSource={failedData}
                      columns={columns}
                      size="small"
                      pagination={false}
                      scroll={{ x: 800 }}
                    />
                  </div>
                )}
              </div>
            ),
            width: 1000,
          });
        }
      } catch (error) {
        message.error('导入失败');
      }
    };
  };

  // 批量选择问卷处理函数
  const handleBatchSelectSurveys = () => {
    setSurveyBatchModalVisible(true);
    setSelectedSurveyKeys([]);
    setSurveyFilterText('');
    setSurveyTypeFilter('');
    fetchBatchSurveys();
  };

  const handleBatchSurveyConfirm = () => {
    const selectedSurveys = batchSurveys.filter(survey => 
      selectedSurveyKeys.includes(survey.id)
    );
    
    // 获取当前表单中已有的问卷
    const currentSurveys = form.getFieldValue('surveys') || [];
    
    // 添加选中的问卷，避免重复
    const newSurveys = selectedSurveys.map((survey, index) => ({
      survey_id: survey.id,
      order: currentSurveys.length + index + 1,
      is_required: true,
      max_times: 1,
      is_active: true
    }));
    
    // 过滤掉已存在的问卷
    const filteredNewSurveys = newSurveys.filter(newSurvey => 
      !currentSurveys.some((existing: any) => existing.survey_id === newSurvey.survey_id)
    );
    
    // 更新表单值
    form.setFieldsValue({
      surveys: [...currentSurveys, ...filteredNewSurveys]
    });
    
    setSurveyBatchModalVisible(false);
    message.success(`已添加 ${filteredNewSurveys.length} 个问卷${filteredNewSurveys.length < selectedSurveys.length ? `，跳过 ${selectedSurveys.length - filteredNewSurveys.length} 个重复问卷` : ''}`);
  };

  // 快速添加单个问卷
  const handleQuickAddSurvey = (surveyId: number) => {
    const currentSurveys = form.getFieldValue('surveys') || [];
    
    // 检查是否已存在
    if (currentSurveys.some((s: any) => s.survey_id === surveyId)) {
      message.warning('该问卷已存在');
      return;
    }
    
    // 添加新问卷
    const newSurvey = {
      survey_id: surveyId,
      order: currentSurveys.length + 1,
      is_required: true,
      max_times: null,
      is_active: true
    };
    
    form.setFieldsValue({
      surveys: [...currentSurveys, newSurvey]
    });
    
    message.success('问卷添加成功');
  };

  // 过滤批量问卷数据
  const getFilteredBatchSurveys = () => {
    return batchSurveys.filter(survey => {
      const matchText = !surveyFilterText || 
        survey.title.toLowerCase().includes(surveyFilterText.toLowerCase()) ||
        (survey.description && survey.description.toLowerCase().includes(surveyFilterText.toLowerCase()));
      
      const matchType = !surveyTypeFilter || survey.type === surveyTypeFilter;
      
      return matchText && matchType;
    });
  };

  return (
    <div style={{ padding: '24px' }}>
      
      <div style={{ marginBottom: 16 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
          添加项目
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={projects}
        rowKey="id"
        loading={loading}
        pagination={{
          pageSize: 10,
        }}
        scroll={{ x: 1600 }}
      />

      <Modal
        title={currentProject ? '编辑项目' : '添加项目'}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={() => setIsModalVisible(false)}
        width={1200}
        style={{ top: 20 }}
      >
        <Form
          form={form}
          layout="vertical"
        >
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="name"
                label="项目名称"
                rules={[{ required: true, message: '请输入项目名称' }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="company_name"
                label="公司名称"
                rules={[{ required: true, message: '请输入公司名称' }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="manager"
                label="项目经理"
                rules={[{ required: true, message: '请输入项目经理' }]}
              >
                {isProjectAdmin ? (
                  <Input disabled value={currentUser?.name} />
                ) : (
                  <Input />
                )}
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="description"
            label="项目描述"
            rules={[{ required: true, message: '请输入项目描述' }]}
          >
            <TextArea rows={3} />
          </Form.Item>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="dateRange"
                label="项目周期"
                rules={[{ required: true, message: '请选择项目周期' }]}
              >
                <RangePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="status"
                label="项目状态"
                rules={[{ required: true, message: '请选择项目状态' }]}
              >
                <Select>
                  <Option value="planning">计划中</Option>
                  <Option value="ongoing">进行中</Option>
                  <Option value="completed">已完成</Option>
                  <Option value="cancelled">已取消</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="budget"
                label="项目预算"
                rules={[{ required: true, message: '请输入项目预算' }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  formatter={value => `¥ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => value!.replace(/\¥\s?|(,*)/g, '')}
                />
              </Form.Item>
            </Col>
          </Row>

          {/* === 新增：项目类型选择 === */}
          <Card title="项目类型配置" style={{ marginBottom: 16 }}>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name="project_type"
                  label="项目类型"
                  rules={[{ required: true, message: '请选择项目类型' }]}
                >
                  <Select>
                    <Option value="survey">问卷项目</Option>
                    <Option value="ambassador">大使项目</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* === 通用：时间段限制配置 === */}
          <Card title="提交限制配置" style={{ marginBottom: 16 }}>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name="participation_limit_type"
                  label="提交限制周期"
                  rules={[{ required: true, message: '请选择提交限制周期' }]}
                >
                  <Select>
                    <Option value="unlimited">无限制</Option>
                    <Option value="daily">每日限制</Option>
                    <Option value="weekly">每周限制</Option>
                    <Option value="monthly">每月限制</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  noStyle
                  shouldUpdate={(prevValues, currentValues) => 
                    prevValues.participation_limit_type !== currentValues.participation_limit_type
                  }
                >
                  {({ getFieldValue }) =>
                    getFieldValue('participation_limit_type') !== 'unlimited' ? (
                      <Form.Item
                        name="participation_max_times"
                        label="最大提交次数"
                        rules={[{ required: true, message: '请输入最大提交次数' }]}
                      >
                        <InputNumber min={1} style={{ width: '100%' }} />
                      </Form.Item>
                    ) : null
                  }
                </Form.Item>
              </Col>
            </Row>

            <div style={{ 
              backgroundColor: '#f6f6f6', 
              padding: '12px', 
              borderRadius: '6px',
              fontSize: '12px',
              color: '#666'
            }}>
              <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) => 
                  prevValues.project_type !== currentValues.project_type ||
                  prevValues.participation_limit_type !== currentValues.participation_limit_type
                }
              >
                {({ getFieldValue }) => {
                  const projectType = getFieldValue('project_type');
                  const limitType = getFieldValue('participation_limit_type');
                  const isAmbassador = projectType === 'ambassador';
                  
                  if (limitType === 'unlimited') {
                    return isAmbassador ? 
                      '• 大使项目：医生可以无限次参与推广活动' :
                      '• 问卷项目：医生可以无限次提交问卷（受问卷自身次数限制）';
                  }
                  
                  const periodText = {
                    'daily': '每日',
                    'weekly': '每周',
                    'monthly': '每月'
                  }[limitType as 'daily' | 'weekly' | 'monthly'] || '';
                  
                  return isAmbassador ? 
                    `• 大使项目：医生${periodText}最多参与指定次数的推广活动` :
                    `• 问卷项目：医生${periodText}最多提交指定次数（问卷级别的次数限制依然有效）`;
                }}
              </Form.Item>
            </div>
          </Card>

          <Card title="访问控制" style={{ marginBottom: 16 }}>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="require_login"
                  label="访问控制"
                  valuePropName="checked"
                >
                  <Checkbox>需要微信登录</Checkbox>
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name="agreement_required"
              label="协议设置"
              valuePropName="checked"
            >
              <Checkbox>需要签署协议</Checkbox>
            </Form.Item>

            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) => prevValues.agreement_required !== currentValues.agreement_required}
            >
              {({ getFieldValue }) =>
                getFieldValue('agreement_required') ? (
                  <>
                    <Form.Item
                      name="agreement_title"
                      label="协议标题"
                      rules={[{ required: true, message: '请输入协议标题' }]}
                    >
                      <Input placeholder="请输入协议标题" />
                    </Form.Item>

                    <Form.Item name="agreement_input_type" label="协议输入方式">
                      <Select defaultValue="text">
                        <Option value="text">文本输入</Option>
                        <Option value="file">文件上传</Option>
                      </Select>
                    </Form.Item>

                    <Form.Item
                      noStyle
                      shouldUpdate={(prevValues, currentValues) => prevValues.agreement_input_type !== currentValues.agreement_input_type}
                    >
                      {({ getFieldValue: innerGetFieldValue }) =>
                        innerGetFieldValue('agreement_input_type') === 'text' ? (
                          <Form.Item
                            name="agreement_content"
                            label="协议内容"
                            rules={[{ required: true, message: '请输入协议内容' }]}
                          >
                            <TextArea rows={6} placeholder="请输入协议内容" />
                          </Form.Item>
                        ) : (
                          <Form.Item
                            name="agreement_content"
                            label="协议文件"
                            rules={[{ required: true, message: '请上传协议文件' }]}
                          >
                            <div>
                              <Upload
                                accept=".pdf,.doc,.docx"
                                showUploadList={true}
                                beforeUpload={(file) => {
                                  uploadAgreementFile(file).then(response => {
                                    console.log('uploadAgreementFile', response);
                                    if (response.success && response.data.url) {
                                      form.setFieldsValue({
                                        agreement_content: response.data.url
                                      });
                                      message.success('文件上传成功');
                                    } else {
                                      message.error('文件上传失败');
                                    }
                                  }).catch(error => {
                                    console.error('文件上传失败:', error);
                                    message.error('文件上传失败');
                                  });
                                  
                                  return false; // 阻止默认上传行为
                                }}
                              >
                                <Button icon={<UploadOutlined />}>上传协议文件</Button>
                              </Upload>
                              {form.getFieldValue('agreement_content') && (
                                <div style={{ marginTop: 8, color: '#52c41a' }}>
                                  ✓ 文件已上传: {form.getFieldValue('agreement_content').split('/').pop()}
                                </div>
                              )}
                            </div>
                          </Form.Item>
                        )
                      }
                    </Form.Item>
                  </>
                ) : null
              }
            </Form.Item>
          </Card>

          <Card title="审核与项目封面" style={{ marginBottom: 16 }}>
            <Form.Item name="review_mode" label="审核方式" initialValue="manual" extra="人工审核必须指定人员；AI审核由定时任务自动通过待审核记录。修改方式适用于尚未审核的记录。"><Select options={[{ value: 'manual', label: '人工审核' }, { value: 'ai', label: 'AI审核（定时自动通过）' }]} /></Form.Item>
            <Form.Item name="kv_image" hidden><Input /></Form.Item>
            <Form.Item noStyle shouldUpdate={(a, b) => a.kv_image !== b.kv_image}>{({ getFieldValue }) => <Form.Item label="项目 KV" extra="展示在手机端项目卡片顶部，建议 16:9 横图，支持 JPG、PNG、WebP，最大 2MB。"><Space direction="vertical"><Space><Upload accept="image/png,image/jpeg,image/webp" showUploadList={false} beforeUpload={file => {
              if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type) || file.size > 2 * 1024 * 1024) { message.error('请选择不超过 2MB 的图片'); return false; }
              uploadImage(file).then(r => { if (r.success && r.url) form.setFieldsValue({ kv_image: r.url }); else message.error('上传失败'); }).catch(() => message.error('上传失败')); return false;
            }}><Button icon={<UploadOutlined />}>上传项目 KV</Button></Upload>{getFieldValue('kv_image') && <Button onClick={() => form.setFieldsValue({ kv_image: null })}>移除</Button>}</Space>{getFieldValue('kv_image') && <img src={getFieldValue('kv_image')} alt="项目KV预览" style={{ width: 280, aspectRatio: '16 / 9', objectFit: 'cover', borderRadius: 12 }} />}</Space></Form.Item>}</Form.Item>
          </Card>
          <Card title="审核人员" style={{ marginBottom: 16 }}>
            <Form.List name="approvers" rules={[{ validator: async (_, value) => { if (form.getFieldValue('review_mode') !== 'ai' && !(value || []).length) throw new Error('人工审核至少指定一名审核人员'); } }]}>
              {(fields, { add, remove }, { errors }) => <>
                {fields.map(({ key, name, ...restField }) => <Space key={key} align="baseline">
                  <Form.Item {...restField} name={[name, 'user_id']} rules={[{ required: true, message: '请选择审核人员' }]}><Select style={{ width: 280 }} showSearch optionFilterProp="label" placeholder="选择审核人员" options={users.map(u => ({ value: u.id, label: u.name }))} /></Form.Item>
                  <Button type="link" onClick={() => remove(name)}>移除</Button>
                </Space>)}
                <Form.Item><Button type="dashed" onClick={() => add({ approver_type: 'primary', order_index: fields.length + 1 })}>添加审核人员</Button><Form.ErrorList errors={errors} /></Form.Item>
                <span style={{ color: '#888' }}>仅被指派的审核员可以处理本项目；多人时任意一人审核即可。</span>
              </>}
            </Form.List>
          </Card>
          {/* 动态显示问卷项目和大使项目的不同配置 */}
          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => prevValues.project_type !== currentValues.project_type}
          >
            {({ getFieldValue }) =>
              getFieldValue('project_type') === 'survey' ? (
                <>
                  {/* 手机端答题页样式 */}
                  <Card title="手机端答题页样式" style={{ marginBottom: 16 }}>
                    <Form.Item name="mobile_bg_image" hidden>
                      <Input />
                    </Form.Item>
                    <Form.Item
                      noStyle
                      shouldUpdate={(prevValues, currentValues) => prevValues.mobile_bg_image !== currentValues.mobile_bg_image}
                    >
                      {({ getFieldValue: getBgFieldValue }) => {
                        const bgUrl = getBgFieldValue('mobile_bg_image');
                        return (
                          <Form.Item
                            label="答题页背景图"
                            tooltip="医生在手机端回答本项目问卷时，会用这张图作为整页固定背景，题目卡片以半透明白底叠加在上面。建议使用竖向图片（如 750×1624），不超过 2MB。不设置则使用默认浅灰背景。"
                          >
                            <Space direction="vertical" size={8}>
                              <Space>
                                <Upload
                                  accept="image/png,image/jpeg,image/jpg,image/webp"
                                  showUploadList={false}
                                  beforeUpload={(file) => {
                                    if (file.size > 2 * 1024 * 1024) {
                                      message.error('背景图大小不能超过 2MB');
                                      return false;
                                    }
                                    uploadImage(file)
                                      .then(response => {
                                        if (response.success && response.url) {
                                          form.setFieldsValue({ mobile_bg_image: response.url });
                                          message.success('背景图上传成功');
                                        } else {
                                          message.error('背景图上传失败');
                                        }
                                      })
                                      .catch(() => message.error('背景图上传失败'));
                                    return false;
                                  }}
                                >
                                  <Button icon={<UploadOutlined />}>
                                    {bgUrl ? '重新上传' : '上传背景图'}
                                  </Button>
                                </Upload>
                                {bgUrl && (
                                  <Button
                                    type="link"
                                    danger
                                    onClick={() => form.setFieldsValue({ mobile_bg_image: null })}
                                  >
                                    移除背景图
                                  </Button>
                                )}
                              </Space>
                              {bgUrl ? (
                                <img
                                  src={bgUrl}
                                  alt="背景图预览"
                                  style={{
                                    width: 110,
                                    borderRadius: 4,
                                    border: '1px solid #f0f0f0',
                                    objectFit: 'cover'
                                  }}
                                />
                              ) : (
                                <span style={{ fontSize: 12, color: '#999' }}>
                                  未设置，手机端使用默认浅灰背景
                                </span>
                              )}
                            </Space>
                          </Form.Item>
                        );
                      }}
                    </Form.Item>
                  </Card>

                  {/* 问卷项目配置 */}
                  <Card title="层级配置" style={{ marginBottom: 16 }}>
                    <Form.List name="level_config">
                      {(fields, { add, remove }) => (
                        <>
                          <div style={{ marginBottom: 8 }}>
                            <span style={{ fontSize: 12, color: '#666' }}>
                              配置白名单医生的组织层级（如：省份、城市、医院等）
                            </span>
                          </div>
                          {fields.map(({ key, name, ...restField }) => (
                            <Row key={key} gutter={16} style={{ marginBottom: 8 }}>
                              <Col span={20}>
                                <Form.Item
                                  {...restField}
                                  name={name}
                                  rules={[{ required: true, message: '请输入层级名称' }]}
                                >
                                  <Input placeholder="输入层级名称（如：省份、城市）" />
                                </Form.Item>
                              </Col>
                              <Col span={4}>
                                <Button type="link" onClick={() => remove(name)}>
                                  删除
                                </Button>
                              </Col>
                            </Row>
                          ))}
                          <Form.Item>
                            <Button type="dashed" onClick={() => add()} block>
                              添加层级
                            </Button>
                          </Form.Item>
                        </>
                      )}
                    </Form.List>
                  </Card>

                  {/* 问卷配置 - 只有问卷项目需要 */}
                  <Card title="问卷配置" style={{ marginBottom: 16 }}>
                    <div style={{ marginBottom: 16 }}>
                      <Space>
                        <Button 
                          type="dashed" 
                          onClick={handleBatchSelectSurveys}
                          icon={<PlusOutlined />}
                        >
                          批量选择问卷
                        </Button>
                        <span style={{ color: '#666', fontSize: 12 }}>
                          推荐：当需要添加多个问卷时使用批量选择
                        </span>
                      </Space>
                    </div>
                    <Form.List name="surveys">
                      {(fields, { add, remove }) => (
                        <>
                          {fields.map(({ key, name, ...restField }) => (
                            <Row key={key} gutter={16} style={{ marginBottom: 8 }}>
                              <Col span={6}>
                                <Form.Item
                                  {...restField}
                                  name={[name, 'survey_id']}
                                  rules={[{ required: true, message: '请选择问卷' }]}
                                >
                                  <Select 
                                    placeholder="选择问卷（输入标题可搜索）"
                                    showSearch
                                    filterOption={false}
                                    allowClear
                                    loading={surveyLoading}
                                    onSearch={(value) => {
                                      setSurveySearchText(value);
                                      
                                      // 清除之前的定时器
                                      if (searchTimeout) {
                                        clearTimeout(searchTimeout);
                                      }
                                      
                                      // 设置新的定时器
                                      const newTimeout = setTimeout(() => {
                                        fetchSurveys(value);
                                      }, 500);
                                      setSearchTimeout(newTimeout);
                                    }}
                                    onClear={() => {
                                      setSurveySearchText('');
                                      fetchSurveys(''); // 传入空字符串立即获取所有问卷
                                    }}
                                    notFoundContent={surveySearchText ? `未找到包含"${surveySearchText}"的问卷` : '暂无问卷'}
                                  >
                                    {(surveys || []).map(survey => (
                                      <Option key={survey.id} value={survey.id}>
                                        {survey.title} - ¥{survey.price}
                                        <Button 
                                          type="link" 
                                          size="small" 
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleQuickAddSurvey(survey.id);
                                          }}
                                          style={{ padding: 0, marginLeft: 8 }}
                                        >
                                          快速添加
                                        </Button>
                                      </Option>
                                    ))}
                                  </Select>
                                </Form.Item>
                              </Col>
                              <Col span={3}>
                                <Form.Item
                                  {...restField}
                                  name={[name, 'order']}
                                  rules={[{ required: true, message: '请输入顺序' }]}
                                >
                                  <InputNumber min={1} placeholder="顺序" />
                                </Form.Item>
                              </Col>
                              <Col span={3}>
                                <Form.Item
                                  {...restField}
                                  name={[name, 'max_times']}
                                >
                                  <InputNumber min={1} placeholder="次数限制" />
                                </Form.Item>
                              </Col>
                              <Col span={4}>
                                <Form.Item
                                  {...restField}
                                  name={[name, 'is_required']}
                                  valuePropName="checked"
                                >
                                  <Checkbox>必填</Checkbox>
                                </Form.Item>
                              </Col>
                              <Col span={4}>
                                <Form.Item
                                  {...restField}
                                  name={[name, 'is_active']}
                                  valuePropName="checked"
                                >
                                  <Checkbox>启用</Checkbox>
                                </Form.Item>
                              </Col>
                              <Col span={4}>
                                <Button type="link" onClick={() => remove(name)}>
                                  删除
                                </Button>
                              </Col>
                            </Row>
                          ))}
                          <Form.Item>
                            <Button type="dashed" onClick={() => add()} block>
                              添加问卷
                            </Button>
                          </Form.Item>
                        </>
                      )}
                    </Form.List>
                  </Card>
                </>
              ) : (
                <>
                  {/* 大使项目配置 */}
                  <Card title="层级配置" style={{ marginBottom: 16 }}>
                    <Form.List name="level_config">
                      {(fields, { add, remove }) => (
                        <>
                          <div style={{ marginBottom: 8 }}>
                            <span style={{ fontSize: 12, color: '#666' }}>
                              配置白名单医生的组织层级（如：省份、城市、医院等）
                            </span>
                          </div>
                          {fields.map(({ key, name, ...restField }) => (
                            <Row key={key} gutter={16} style={{ marginBottom: 8 }}>
                              <Col span={20}>
                                <Form.Item
                                  {...restField}
                                  name={name}
                                  rules={[{ required: true, message: '请输入层级名称' }]}
                                >
                                  <Input placeholder="输入层级名称（如：省份、城市）" />
                                </Form.Item>
                              </Col>
                              <Col span={4}>
                                <Button type="link" onClick={() => remove(name)}>
                                  删除
                                </Button>
                              </Col>
                            </Row>
                          ))}
                          <Form.Item>
                            <Button type="dashed" onClick={() => add()} block>
                              添加层级
                            </Button>
                          </Form.Item>
                        </>
                      )}
                    </Form.List>
                  </Card>

                  <Card title="大使项目配置" style={{ marginBottom: 16 }}>
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item
                          name={['surveys', 0, 'survey_id']}
                          label="关联问卷"
                          rules={[{ required: true, message: '请选择关联问卷' }]}
                        >
                          <Select 
                            placeholder="选择关联问卷（用于计算劳务费）"
                            showSearch
                            filterOption={false}
                            allowClear
                            loading={surveyLoading}
                            onSearch={(value) => {
                              setSurveySearchText(value);
                              
                              // 清除之前的定时器
                              if (searchTimeout) {
                                clearTimeout(searchTimeout);
                              }
                              
                              // 设置新的定时器
                              const newTimeout = setTimeout(() => {
                                fetchSurveys(value);
                              }, 500);
                              setSearchTimeout(newTimeout);
                            }}
                            onClear={() => {
                              setSurveySearchText('');
                              fetchSurveys('');
                            }}
                            notFoundContent={surveySearchText ? `未找到包含"${surveySearchText}"的问卷` : '暂无问卷'}
                            onChange={(surveyId) => {
                              // 自动设置大使项目的问卷配置
                              const selectedSurvey = surveys.find(s => s.id === surveyId);
                              if (selectedSurvey) {
                                form.setFieldsValue({
                                  surveys: [{
                                    survey_id: surveyId,
                                    order: 1,
                                    is_required: true,
                                    max_times: null,
                                    is_active: true
                                  }]
                                });
                              }
                            }}
                          >
                            {(surveys || []).map(survey => (
                              <Option key={survey.id} value={survey.id}>
                                {survey.title} - ¥{survey.price}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                    </Row>
                    
                    <div style={{ padding: 16, backgroundColor: '#f6f6f6', borderRadius: 6 }}>
                      <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: '600' }}>
                        大使项目说明
                      </h4>
                      <div style={{ fontSize: '12px', color: '#666', lineHeight: '1.4' }}>
                        • 大使项目无需配置复杂的问卷流程和审批流程<br />
                        • 医生参与后将自动记录，无需人工审批<br />
                        • 关联问卷仅用于获取劳务费价格，医生不需要填写问卷<br />
                        • 参与限制将控制医生在指定周期内的参与次数<br />
                        • 每次参与将按关联问卷的价格计算劳务费
                      </div>
                    </div>
                  </Card>
                </>
              )
            }
          </Form.Item>
        </Form>
      </Modal>
      
      {/* QR码模态框 */}
      {selectedProject && (
        <ProjectQRCode
          project={selectedProject}
          visible={qrCodeModalVisible}
          onClose={() => {
            setQrCodeModalVisible(false);
            setSelectedProject(null);
          }}
        />
      )}

      {memberProject && <ProjectMembers project={memberProject} onClose={() => { setMemberProject(null); fetchProjects(); }} />}
      {/* 医生提交次数配额抽屉 */}
      <DoctorQuotaDrawer
        open={quotaDrawerOpen}
        projectId={quotaProject?.id ?? null}
        projectName={quotaProject?.name}
        onClose={handleCloseQuotaDrawer}
      />

      {/* 批量选择问卷模态框 */}
      <Modal
        title="批量选择问卷"
        open={surveyBatchModalVisible}
        onOk={handleBatchSurveyConfirm}
        onCancel={() => setSurveyBatchModalVisible(false)}
        width={1000}
        okText={`确定添加 (${selectedSurveyKeys.length})`}
        okButtonProps={{ disabled: selectedSurveyKeys.length === 0 }}
      >
        <div style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col span={12}>
              <Input
                placeholder="搜索问卷标题或描述"
                value={surveyFilterText}
                onChange={(e) => setSurveyFilterText(e.target.value)}
                prefix={<SearchOutlined />}
                allowClear
              />
            </Col>
            <Col span={8}>
              <Select
                placeholder="筛选问卷类型"
                value={surveyTypeFilter}
                onChange={setSurveyTypeFilter}
                allowClear
                style={{ width: '100%' }}
              >
                <Option value="research">研究型</Option>
                <Option value="satisfaction">满意度</Option>
                <Option value="evaluation">评估型</Option>
                <Option value="feedback">反馈型</Option>
                <Option value="other">其他</Option>
              </Select>
            </Col>
            <Col span={4}>
              <div style={{ textAlign: 'right', color: '#666', fontSize: 12 }}>
                <div>共 {getFilteredBatchSurveys().length} 个问卷</div>
                <div>已选 {selectedSurveyKeys.length} 个</div>
              </div>
            </Col>
          </Row>
        </div>
        
        <div style={{ marginBottom: 8, fontSize: 12, color: '#666' }}>
          <span>提示：只能选择已发布状态的问卷</span>
          <span style={{ marginLeft: 16 }}>
            <Button 
              type="link" 
              size="small" 
              onClick={() => {
                const publishedSurveys = getFilteredBatchSurveys().filter(s => s.status === 'published');
                setSelectedSurveyKeys(publishedSurveys.map(s => s.id));
              }}
            >
              全选已发布
            </Button>
            <Button 
              type="link" 
              size="small" 
              onClick={() => setSelectedSurveyKeys([])}
            >
              清空选择
            </Button>
          </span>
        </div>
        
        <Table
          dataSource={getFilteredBatchSurveys()}
          columns={[
            {
              title: 'ID',
              dataIndex: 'id',
              key: 'id',
              width: 60,
            },
            {
              title: '问卷标题',
              dataIndex: 'title',
              key: 'title',
              ellipsis: true,
              render: (text, record) => (
                <div>
                  <div style={{ fontWeight: 'bold' }}>{text}</div>
                  {record.description && (
                    <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                      {record.description.length > 100 ? 
                        `${record.description.substring(0, 100)}...` : 
                        record.description
                      }
                    </div>
                  )}
                </div>
              ),
            },
            {
              title: '类型',
              dataIndex: 'type',
              key: 'type',
              width: 80,
              render: (type) => {
                const typeMap = {
                  research: '研究型',
                  satisfaction: '满意度',
                  evaluation: '评估型',
                  feedback: '反馈型',
                  other: '其他'
                };
                return <Tag>{typeMap[type as keyof typeof typeMap] || type}</Tag>;
              },
            },
            {
              title: '状态',
              dataIndex: 'status',
              key: 'status',
              width: 80,
              render: (status) => {
                const statusMap = {
                  draft: { color: 'default', text: '草稿' },
                  published: { color: 'green', text: '已发布' },
                  archived: { color: 'gray', text: '已归档' }
                };
                const statusInfo = statusMap[status as keyof typeof statusMap] || { color: 'default', text: status };
                return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
              },
            },
            {
              title: '价格',
              dataIndex: 'price',
              key: 'price',
              width: 80,
              render: (price) => price ? `¥${price}` : '免费',
            },
            {
              title: '操作',
              key: 'action',
              width: 100,
              render: (_, record) => {
                const currentSurveys = form.getFieldValue('surveys') || [];
                const isAdded = currentSurveys.some((s: any) => s.survey_id === record.id);
                
                return (
                  <Button
                    type={isAdded ? "default" : "link"}
                    size="small"
                    disabled={record.status !== 'published' || isAdded}
                    onClick={() => handleQuickAddSurvey(record.id)}
                  >
                    {isAdded ? '已添加' : '快速添加'}
                  </Button>
                );
              },
            },
          ]}
          rowKey="id"
          rowSelection={{
            selectedRowKeys: selectedSurveyKeys,
            onChange: setSelectedSurveyKeys,
            getCheckboxProps: (record) => ({
              disabled: record.status !== 'published', // 只能选择已发布的问卷
            }),
          }}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `显示 ${range[0]}-${range[1]} 条，共 ${total} 条`,
          }}
          loading={batchLoading}
          size="small"
        />
      </Modal>
    </div>
  );
};

export default ProjectManagement; 
